import { cloneElement, ReactElement } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

type TabButtonSize = 'sm' | 'md';

export type TabButtonOption<T extends string> = {
  value: T;
  label: string;
  icon?: ReactElement<IconProps>;
};

export type TabButtonProps<T extends string> = {
  options: TabButtonOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: TabButtonSize;
};

export function TabButton<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
}: TabButtonProps<T>) {
  return (
    <Group $size={size}>
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <Tab
            key={option.value}
            type="button"
            aria-pressed={isActive}
            $size={size}
            onClick={() => !isActive && onChange(option.value)}
          >
            {option.icon && cloneElement(option.icon, { size: size === 'md' ? 24 : 16 })}
            {option.label}
          </Tab>
        );
      })}
    </Group>
  );
}

const Group = styled.div<{ $size: TabButtonSize }>((p) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: pxToRem(2),
  padding: p.$size === 'md' ? pxToRem(4) : pxToRem(2),
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
}));

const Tab = styled.button<{ $size: TabButtonSize }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: pxToRem(4),
  height: p.$size === 'md' ? pxToRem(32) : pxToRem(28),
  padding: `0 ${pxToRem(12)} 0 ${pxToRem(8)}`,
  border: 'none',
  borderRadius: pxToRem(6),
  background: 'transparent',
  cursor: 'pointer',
  font: theme.font.semibold14,
  color: theme.color.textHelper,
  whiteSpace: 'nowrap',
  transition: transitionStandard('color', 'background'),

  '&[aria-pressed="false"]:hover, &:focus-visible': {
    background: theme.color.uiTrans10,
    color: theme.color.textSecondary,
  },

  '&[aria-pressed="true"]': {
    background: theme.color.backgroundLightDark,
    color: theme.color.elementBlue100,
    cursor: 'default',
  },
}));
